import React, { useState, useCallback, useEffect } from "react";
import { useDropzone } from "react-dropzone";
import { Images } from "./components/Images";
import {
  processImages,
  initializeModel,
  getModelInfo,
  getLastRuntimeInfo,
  getRuntimeErrorDetails,
  type RuntimeDiagnosticDetails,
} from "../lib/process";
import {
  logDiagnostic,
  getDiagnosticLog,
} from "./lib/diagnostics";
import { DiagnosticsDrawer } from "./components/DiagnosticsDrawer";

interface AppError {
  message: string;
}

export interface ImageFile {
  id: number;
  file: File;
  processedFile?: File;
  error?: string;
  errorDetails?: RuntimeDiagnosticDetails;
  errorReport?: string;
}

async function processSingleImage(file: File): Promise<File> {
  const result = await processImages([file]);

  if (!result || result.length === 0) {
    throw new Error('Failed to process image. Your device may not support this feature.');
  }

  return result[0];
}

function buildFailureReport(
  message: string,
  err: unknown,
  file?: File,
): { details?: RuntimeDiagnosticDetails; report: string } {
  const details = getRuntimeErrorDetails(err);
  const runtime = getLastRuntimeInfo();
  const lines: string[] = [];
  lines.push("PrivateBG iPhone runtime failure report");
  lines.push("Generated: " + new Date().toISOString());
  lines.push("Message: " + message);
  lines.push("URL: " + (typeof location !== "undefined" ? location.href : "n/a"));
  lines.push("");
  lines.push("=== Required fields ===");
  lines.push("failedPhase: " + (details?.failedPhase ?? String(runtime.phase ?? "unknown")));
  lines.push("elapsedMs: " + (details?.elapsedMs ?? String(runtime.elapsedMs ?? "unknown")));
  lines.push("lastCompletedStep: " + (details?.lastCompletedStep ?? String(runtime.lastCompletedStep ?? "unknown")));
  lines.push("modelSelected: " + (details?.modelSelected ?? String(runtime.modelSelected ?? runtime.modelId ?? "unknown")));
  lines.push("isIOS: " + String(details?.isIOS ?? runtime.isIOS ?? "unknown"));
  lines.push("userAgent: " + (details?.userAgent ?? navigator.userAgent));
  lines.push("crossOriginIsolated: " + String(details?.crossOriginIsolated ?? (globalThis as { crossOriginIsolated?: boolean }).crossOriginIsolated ?? "unknown"));
  lines.push("typeof SharedArrayBuffer: " + (details?.sharedArrayBufferType ?? typeof SharedArrayBuffer));
  if (details?.image) {
    lines.push("image: " + JSON.stringify(details.image));
  } else if (file) {
    lines.push("image: " + JSON.stringify({
      fileName: file.name,
      fileType: file.type,
      fileSize: file.size,
    }));
  }
  lines.push("");
  lines.push("=== Runtime details ===");
  lines.push(JSON.stringify(details ?? runtime, null, 2));
  if (err instanceof Error) {
    lines.push("");
    lines.push("=== Error stack ===");
    lines.push(err.stack ?? err.message);
  }
  return { details, report: lines.join("\n") };
}

export default function App() {
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<AppError | null>(null);
  const [isWebGPU, setIsWebGPU] = useState(false);
  const [isIOS, setIsIOS] = useState(false);
  const [currentModel, setCurrentModel] = useState<'briaai/RMBG-1.4' | 'Xenova/modnet'>('briaai/RMBG-1.4');
  const [isModelSwitching, setIsModelSwitching] = useState(false);
  const [images, setImages] = useState<ImageFile[]>([]);
  const [isDiagOpen, setIsDiagOpen] = useState(false);
  // Incremented whenever we log a diagnostic, plus seeded from any log that
  // survived a page reload. Drives the "Diagnostics (N)" button visibility.
  const [diagBadgeCount, setDiagBadgeCount] = useState(0);

  useEffect(() => {
    const { isIOS: isIOSDevice } = getModelInfo();
    setIsIOS(isIOSDevice);
    setIsLoading(false);
    // Show the Diagnostics button if we already have errors from a previous
    // session — that's exactly the "reload and lost the error" case.
    setDiagBadgeCount(getDiagnosticLog().length);
  }, []);

  const handleModelChange = async (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newModel = event.target.value as typeof currentModel;
    setIsModelSwitching(true);
    setError(null);
    try {
      const initialized = await initializeModel(newModel);
      if (!initialized) {
        throw new Error("Failed to initialize new model");
      }
      setCurrentModel(newModel);
    } catch (err) {
      if (err instanceof Error && err.message.includes("Falling back")) {
        setCurrentModel('briaai/RMBG-1.4');
      } else {
        logDiagnostic("handleModelChange", err, {
          runtime: getLastRuntimeInfo(),
        });
        setDiagBadgeCount(c => c + 1);
        setError({
          message: err instanceof Error ? err.message : "Failed to switch models"
        });
      }
    } finally {
      setIsModelSwitching(false);
    }
  };

  const onDrop = useCallback(async (acceptedFiles: File[]) => {
    const newImages = acceptedFiles.map((file, index) => ({
      id: Date.now() + index,
      file,
      processedFile: undefined
    }));
    setImages(prev => [...prev, ...newImages]);

    if (images.length === 0) {
      setIsLoading(true);
      setError(null);
      try {
        const initialized = await initializeModel();
        if (!initialized) {
          throw new Error("Failed to initialize background removal model");
        }
        const { isWebGPUSupported } = getModelInfo();
        setIsWebGPU(isWebGPUSupported);
      } catch (err) {
        const message = err instanceof Error ? err.message : "An unknown error occurred";
        const failure = buildFailureReport(message, err, newImages[0]?.file);
        logDiagnostic("initializeModel", err, {
          runtime: getLastRuntimeInfo(),
          details: failure.details,
          phase: "on-first-drop",
        });
        setError({ message });
        // Previously `setImages([])` wiped the grid on init failure — which
        // made the error impossible to see next to the image the user just
        // uploaded. Instead, attach the init error to every newly-dropped
        // image card so the failure is co-located with the action.
        setImages(prev => {
          const newById = new Map(newImages.map(ni => [ni.id, ni]));

          // Update any dropped cards already present in state.
          const updated = prev.map(img => {
            if (newById.has(img.id)) {
              return {
                ...img,
                error: message,
                errorDetails: failure.details,
                errorReport: failure.report,
                processedFile: undefined,
              };
            }
            return img;
          });

          // If init failed very quickly, React may not have committed the
          // preceding `setImages([...prev, ...newImages])` yet. Ensure each
          // dropped image still gets an error card instead of disappearing.
          const seenIds = new Set(updated.map(img => img.id));
          const missing = newImages
            .filter(ni => !seenIds.has(ni.id))
            .map(ni => ({
              ...ni,
              error: message,
              errorDetails: failure.details,
              errorReport: failure.report,
            }));

          return [...updated, ...missing];
        });
        setDiagBadgeCount(c => c + 1);
        setIsLoading(false);
        return;
      }
      setIsLoading(false);
    }

    for (const image of newImages) {
      try {
        const processed = await processSingleImage(image.file);
        setImages(prev => prev.map(img =>
          img.id === image.id
            ? {
              ...img,
              processedFile: processed,
              error: undefined,
              errorDetails: undefined,
              errorReport: undefined,
            }
            : img
        ));
      } catch (err) {
        const message = err instanceof Error
          ? err.message
          : 'Failed to process image. Your device may not support this feature.';
        const failure = buildFailureReport(message, err, image.file);
        console.error('Error processing image:', err);
        logDiagnostic("processImage", err, {
          runtime: getLastRuntimeInfo(),
          details: failure.details,
          fileName: image.file.name,
          fileSize: image.file.size,
          fileType: image.file.type,
        });
        setDiagBadgeCount(c => c + 1);
        // Attach the error to the specific image card instead of filtering it
        // out — see issue #3. Removing the image hid the failure and left the
        // user with no way to see what went wrong.
        setImages(prev => prev.map(img =>
          img.id === image.id
            ? {
              ...img,
              error: message,
              errorDetails: failure.details,
              errorReport: failure.report,
              processedFile: undefined,
            }
            : img
        ));
      }
    }
  }, [images.length]);

  const handleRetry = useCallback(async (id: number) => {
    const target = images.find(img => img.id === id);
    if (!target) return;

    // Clear prior error + mark as processing again by stripping processedFile.
    setImages(prev => prev.map(img =>
      img.id === id
        ? { ...img, error: undefined, errorDetails: undefined, errorReport: undefined, processedFile: undefined }
        : img
    ));

    try {
      const processed = await processSingleImage(target.file);
      setImages(prev => prev.map(img =>
        img.id === id
          ? {
            ...img,
            processedFile: processed,
            error: undefined,
            errorDetails: undefined,
            errorReport: undefined,
          }
          : img
      ));
    } catch (err) {
      const message = err instanceof Error
        ? err.message
        : 'Failed to process image. Your device may not support this feature.';
      const failure = buildFailureReport(message, err, target.file);
      console.error('Error retrying image:', err);
      logDiagnostic("processImage:retry", err, {
        runtime: getLastRuntimeInfo(),
        details: failure.details,
        fileName: target.file.name,
        fileSize: target.file.size,
        fileType: target.file.type,
      });
      setDiagBadgeCount(c => c + 1);
      setImages(prev => prev.map(img =>
        img.id === id
          ? {
            ...img,
            error: message,
            errorDetails: failure.details,
            errorReport: failure.report,
            processedFile: undefined,
          }
          : img
      ));
    }
  }, [images]);

  const handlePaste = async (event: React.ClipboardEvent) => {
    const clipboardItems = event.clipboardData.items;
    const imageFiles: File[] = [];
    for (const item of clipboardItems) {
      if (item.type.startsWith("image")) {
        const file = item.getAsFile();
        if (file) {
          imageFiles.push(file);
        }
      }
    }
    if (imageFiles.length > 0) {
      onDrop(imageFiles);
    }
  };

  const {
    getRootProps,
    getInputProps,
    isDragActive,
    isDragAccept,
    isDragReject,
  } = useDropzone({
    onDrop,
    accept: {
      "image/*": [".jpeg", ".jpg", ".png"],
    },
  });

  return (
    <div className="min-h-screen bg-gray-50" onPaste={handlePaste}>
      <DiagnosticsDrawer isOpen={isDiagOpen} onClose={() => setIsDiagOpen(false)} />
      {/* Nav */}
      <nav className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <svg className="w-7 h-7 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
              </svg>
              <span className="text-2xl font-bold text-gray-800">PrivateBG</span>
            </div>
            {diagBadgeCount > 0 && (
              <button
                onClick={() => setIsDiagOpen(true)}
                className="flex items-center gap-1 px-3 py-1.5 bg-red-50 text-red-700 border border-red-200 rounded-md hover:bg-red-100 transition-colors text-sm"
                title="View error diagnostics"
              >
                <svg
                  className="w-4 h-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                  strokeWidth="2"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                  />
                </svg>
                <span>Diagnostics ({diagBadgeCount})</span>
              </button>
            )}
            {!isIOS && (
              <div className="flex items-center gap-4">
                <span className="text-gray-600 text-sm">Model:</span>
                <select
                  value={currentModel}
                  onChange={handleModelChange}
                  className="bg-white border border-gray-300 rounded-md px-3 py-1 text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-brand-500 focus:border-brand-500"
                  disabled={!isWebGPU}
                >
                  <option value="briaai/RMBG-1.4">RMBG-1.4 (Cross-browser)</option>
                  {isWebGPU && (
                    <option value="Xenova/modnet">MODNet (WebGPU)</option>
                  )}
                </select>
              </div>
            )}
          </div>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Hero + Trust Badges */}
        {images.length === 0 && (
          <div className="text-center mb-8">
            <h1 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-3">
              Remove Any Background. 100% Private.
            </h1>
            <p className="text-xl text-gray-600 mb-6">
              No upload to any server. AI runs entirely in your browser.
            </p>
            <div className="flex flex-wrap justify-center gap-4 sm:gap-6 mb-2 text-sm text-gray-600">
              <span className="flex items-center gap-1.5">
                <svg className="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                </svg>
                100% Private
              </span>
              <span className="flex items-center gap-1.5">
                <svg className="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5.636 5.636a9 9 0 1012.728 0M12 3v9" />
                </svg>
                No Server Upload
              </span>
              <span className="flex items-center gap-1.5">
                <svg className="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 17.25v1.007a3 3 0 01-.879 2.122L7.5 21h9l-.621-.621A3 3 0 0115 18.257V17.25m6-12V15a2.25 2.25 0 01-2.25 2.25H5.25A2.25 2.25 0 013 15V5.25A2.25 2.25 0 015.25 3h13.5A2.25 2.25 0 0121 5.25z" />
                </svg>
                Runs on Your Device
              </span>
              <span className="flex items-center gap-1.5">
                <svg className="w-4 h-4 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Completely Free
              </span>
            </div>
          </div>
        )}

        {/* Tool: Dropzone */}
        <div className="w-full max-w-3xl mx-auto">
          <div
            {...getRootProps()}
            className={`p-8 mb-8 border-2 border-dashed rounded-lg text-center cursor-pointer transition-colors duration-300 ease-in-out bg-white
              ${isDragAccept ? "border-green-500 bg-green-50" : ""}
              ${isDragReject ? "border-red-500 bg-red-50" : ""}
              ${isDragActive ? "border-brand-500 bg-brand-50" : "border-gray-300 hover:border-brand-500 hover:bg-brand-50"}
              ${isLoading || isModelSwitching ? "cursor-not-allowed" : ""}
            `}
          >
            <input {...getInputProps()} className="hidden" disabled={isLoading || isModelSwitching} />
            <div className="flex flex-col items-center gap-2">
              {isLoading || isModelSwitching ? (
                <>
                  <div className="inline-block animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-brand-600 mb-2"></div>
                  <p className="text-lg text-gray-600">
                    {isModelSwitching ? 'Switching models...' : 'Loading background removal model...'}
                  </p>
                </>
              ) : error ? (
                <>
                  <svg className="w-12 h-12 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                  </svg>
                  <p className="text-lg text-red-600 font-medium mb-2">{error.message}</p>
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsDiagOpen(true);
                    }}
                    className="text-sm text-red-700 underline mb-2"
                  >
                    View diagnostics &rarr;
                  </button>
                  {currentModel === 'Xenova/modnet' && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleModelChange({ target: { value: 'briaai/RMBG-1.4' }} as any);
                      }}
                      className="px-4 py-2 bg-brand-600 text-white rounded-md hover:bg-brand-700 transition-colors"
                    >
                      Switch to Cross-browser Version
                    </button>
                  )}
                </>
              ) : (
                <>
                  <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <p className="text-lg text-gray-600">
                    {isDragActive
                      ? "Drop the images here..."
                      : "Drag and drop images here"}
                  </p>
                  <p className="text-sm text-gray-500">or click to select files (JPG, PNG)</p>
                </>
              )}
            </div>
          </div>

          {images.length === 0 && isIOS && (
            <div className="bg-brand-50 border border-brand-200 rounded-lg p-4 text-sm text-brand-800">
              On iPhone/iPad, the first image may take 20&ndash;30 seconds to process. Please wait.
            </div>
          )}

          {images.length === 0 && (
            <p className="text-sm text-gray-500 text-center mt-4">
              Your images are processed 100% locally. Nothing is uploaded. Ever.
            </p>
          )}
        </div>

        {/* Processed Images */}
        <div className="max-w-7xl mx-auto">
          <Images
            images={images}
            onDelete={(id) => setImages(prev => prev.filter(img => img.id !== id))}
            onRetry={handleRetry}
            onViewDiagnostics={() => setIsDiagOpen(true)}
          />
        </div>

        {/* Ad Zone: Below Tool */}
        <div id="ad-below-tool" className="max-w-3xl mx-auto my-8" data-ad-slot="ad-below-tool" data-ad-format="horizontal"></div>

        {/* SEO Content: Below the Fold */}
        {images.length === 0 && (
          <>
            {/* How It Works */}
            <section className="max-w-5xl mx-auto py-12">
              <h2 className="text-3xl font-bold text-gray-900 text-center mb-10">How It Works</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="text-center">
                  <div className="w-16 h-16 bg-brand-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5m-13.5-9L12 3m0 0l4.5 4.5M12 3v13.5" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">1. Upload Your Image</h3>
                  <p className="text-gray-600">Drag, drop, or paste any JPG or PNG image directly into your browser.</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-brand-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">2. AI Removes Background</h3>
                  <p className="text-gray-600">Our AI model processes your image right in your browser. Nothing leaves your device.</p>
                </div>
                <div className="text-center">
                  <div className="w-16 h-16 bg-brand-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <svg className="w-8 h-8 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 005.25 21h13.5A2.25 2.25 0 0021 18.75V16.5M16.5 12L12 16.5m0 0L7.5 12m4.5 4.5V3" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">3. Download Result</h3>
                  <p className="text-gray-600">Get your transparent PNG instantly. Use the editor to add custom background colors.</p>
                </div>
              </div>
            </section>

            {/* Ad Zone: Mid Content */}
            <div id="ad-mid-content" className="max-w-3xl mx-auto my-4" data-ad-slot="ad-mid-content" data-ad-format="horizontal"></div>

            {/* Why Private */}
            <section className="max-w-5xl mx-auto py-12">
              <h2 className="text-3xl font-bold text-gray-900 text-center mb-10">Why Choose a Private Background Remover?</h2>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <div className="w-12 h-12 bg-brand-100 rounded-lg flex items-center justify-center mb-4">
                    <svg className="w-6 h-6 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M16.5 10.5V6.75a4.5 4.5 0 10-9 0v3.75m-.75 11.25h10.5a2.25 2.25 0 002.25-2.25v-6.75a2.25 2.25 0 00-2.25-2.25H6.75a2.25 2.25 0 00-2.25 2.25v6.75a2.25 2.25 0 002.25 2.25z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">No Upload Required</h3>
                  <p className="text-gray-600">Your images stay on your device. Period. Unlike other tools that upload to servers, we process everything locally using AI that runs in your browser.</p>
                </div>
                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <div className="w-12 h-12 bg-brand-100 rounded-lg flex items-center justify-center mb-4">
                    <svg className="w-6 h-6 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 6a3.75 3.75 0 11-7.5 0 3.75 3.75 0 017.5 0zM4.501 20.118a7.5 7.5 0 0114.998 0A17.933 17.933 0 0112 21.75c-2.676 0-5.216-.584-7.499-1.632z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">No Account Needed</h3>
                  <p className="text-gray-600">No sign-up, no email, no payment information. Just open the page and start removing backgrounds immediately.</p>
                </div>
                <div className="bg-white rounded-lg p-6 shadow-sm">
                  <div className="w-12 h-12 bg-brand-100 rounded-lg flex items-center justify-center mb-4">
                    <svg className="w-6 h-6 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                      <path strokeLinecap="round" strokeLinejoin="round" d="M8.288 15.038a5.25 5.25 0 017.424 0M5.106 11.856c3.807-3.808 9.98-3.808 13.788 0M1.924 8.674c5.565-5.565 14.587-5.565 20.152 0M12.53 18.22l-.53.53-.53-.53a.75.75 0 011.06 0z" />
                    </svg>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-2">Works Offline</h3>
                  <p className="text-gray-600">After the AI model loads once, you can disconnect from the internet and keep removing backgrounds. The model is cached in your browser.</p>
                </div>
              </div>
            </section>

            {/* FAQ */}
            <section className="max-w-3xl mx-auto py-12">
              <h2 className="text-3xl font-bold text-gray-900 text-center mb-10">Frequently Asked Questions</h2>
              <div className="space-y-4">
                <FaqItem
                  question="Is this background remover really private?"
                  answer="Yes. The AI model runs entirely in your browser using WebAssembly. Your images are never uploaded to any server. You can verify this by using the tool with your internet disconnected after the first load."
                />
                <FaqItem
                  question="How does background removal work without uploading?"
                  answer="We use RMBG-1.4, a state-of-the-art AI model that runs directly in your browser via WebAssembly and Transformers.js. The model is downloaded once and cached, then all processing happens locally on your device."
                />
                <FaqItem
                  question="Is this background remover free?"
                  answer="Yes, completely free with no limits. Since processing happens on your device, there are no server costs to pass on to users."
                />
                {/* Ad Zone: In FAQ */}
                <div id="ad-in-faq" className="my-4" data-ad-slot="ad-in-faq" data-ad-format="rectangle"></div>
                <FaqItem
                  question="What image formats are supported?"
                  answer="PrivateBG supports JPG, JPEG, and PNG image formats. Results are always downloaded as high-quality PNG files with transparent backgrounds."
                />
                <FaqItem
                  question="Does it work on mobile?"
                  answer="Yes, it works on mobile browsers. Performance depends on your device's processing power. Modern phones handle it well, though processing may take a few seconds longer than on desktop."
                />
                <FaqItem
                  question="How accurate is the AI background removal?"
                  answer="The RMBG-1.4 model provides professional-quality results for most images including people, products, and objects. Complex edges like hair and fur are handled well in most cases."
                />
              </div>
            </section>

            {/* Footer */}
            <footer className="max-w-5xl mx-auto py-8 border-t border-gray-200 text-center">
              {/* Ad Zone: Footer */}
              <div id="ad-footer" className="max-w-3xl mx-auto mb-6" data-ad-slot="ad-footer" data-ad-format="horizontal"></div>
              <div className="flex items-center justify-center gap-2 mb-3">
                <svg className="w-5 h-5 text-brand-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2">
                  <path strokeLinecap="round" strokeLinejoin="round" d="M9 12.75L11.25 15 15 9.75m-3-7.036A11.959 11.959 0 013.598 6 11.99 11.99 0 003 9.749c0 5.592 3.824 10.29 9 11.623 5.176-1.332 9-6.03 9-11.622 0-1.31-.21-2.571-.598-3.751h-.152c-3.196 0-6.1-1.248-8.25-3.285z" />
                </svg>
                <span className="font-semibold text-gray-800">PrivateBG</span>
              </div>
              <p className="text-sm text-gray-500 mb-2">Your images never leave your device.</p>
              <p className="text-xs text-gray-400">
                Powered by <a href="https://huggingface.co/docs/transformers.js" className="underline hover:text-gray-600" target="_blank" rel="noopener noreferrer">Transformers.js</a> & <a href="https://huggingface.co/briaai/RMBG-1.4" className="underline hover:text-gray-600" target="_blank" rel="noopener noreferrer">RMBG-1.4</a>
              </p>
            </footer>
          </>
        )}
      </main>
    </div>
  );
}

function FaqItem({ question, answer }: { question: string; answer: string }) {
  const [isOpen, setIsOpen] = useState(false);
  return (
    <div className="bg-white rounded-lg shadow-sm">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full text-left px-6 py-4 flex items-center justify-between"
      >
        <span className="font-medium text-gray-800">{question}</span>
        <svg
          className={`w-5 h-5 text-gray-500 transition-transform ${isOpen ? 'rotate-180' : ''}`}
          fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth="2"
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
        </svg>
      </button>
      {isOpen && (
        <div className="px-6 pb-4">
          <p className="text-gray-600">{answer}</p>
        </div>
      )}
    </div>
  );
}
